import { ButtonHTMLAttributes, useMemo, useState } from 'react';

import { useIsMutating } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import { getCustomTableMutationKey } from '@queries';
import { activeSwingIdAtom, activeUserIdAtom, layoutCustomizationsAtom, activityAtom } from '@store';
import { colors, LayoutIcon, u } from '@ui';

import { ModuleBase } from '../ModuleBase';
import { ParameterSelectionModal } from '../ParameterSelectionModal/ParameterSelectionModal';

import * as css from './TablePanel.css';
import { TablePresenter } from './TablePresenter';

type TablePanelProps = {
    nodeId: string;
    isEditing?: boolean;
};

export const TablePanel = ({ nodeId, isEditing }: TablePanelProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const activeSwingId = useAtomValue(activeSwingIdAtom);
    const activeUserId = useAtomValue(activeUserIdAtom);
    const customizations = useAtomValue(layoutCustomizationsAtom);
    const activity = useAtomValue(activityAtom);

    const isMutating = useIsMutating({ mutationKey: getCustomTableMutationKey(activity?.layoutId ?? -1, nodeId) }) > 0;
    const parameterIds = useMemo(
        () =>
            customizations
                ?.filter((customization) => customization.parentUiNodeId === nodeId)
                .map(({ childUiNodeId }) => childUiNodeId),
        [customizations, nodeId],
    );

    return (
        <>
            <ModuleBase
                title={'Parameter table'}
                isClosed={isEditing}
                button={
                    <SettingsButton
                        disabled={isMutating}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!isMutating) {
                                setIsModalOpen(true);
                            }
                        }}
                    />
                }
            >
                <TablePresenter nodeId={nodeId} />
            </ModuleBase>
            {isModalOpen ? (
                <ParameterSelectionModal
                    layoutId={activity?.layoutId ?? -1}
                    activeUserId={activeUserId}
                    activeSwingId={activeSwingId}
                    parameterIds={parameterIds ?? []}
                    isOpen={isModalOpen}
                    nodeId={nodeId}
                    onDismiss={() => setIsModalOpen(false)}
                />
            ) : null}
        </>
    );
};

const SettingsButton = ({
    onClick,
    disabled,
}: Pick<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'disabled'>) => {
    return (
        <button onClick={onClick} className={css.settingsButton({ disabled })}>
            <LayoutIcon weight="fill" color={disabled ? colors.blue[300] : colors.blue[500]} size={u(2)} />
            settings
        </button>
    );
};
